'use client'

import * as React from 'react'
import * as CheckboxPrimitive from '@radix-ui/react-checkbox'
import { Check, Minus } from 'lucide-react'
import { cn } from '@/lib/utils'

/**
 * Checkbox — Radix-backed, replaces hand-rolled <input type="checkbox">.
 * The 16px box never stands alone: compose it in a row with a Label
 * (htmlFor ↔ id) at min-h-11 so the whole row is a 44px click target.
 * Supports checked={'indeterminate'} for future select-all rows.
 */
const Checkbox = React.forwardRef<
    React.ElementRef<typeof CheckboxPrimitive.Root>,
    React.ComponentPropsWithoutRef<typeof CheckboxPrimitive.Root>
>(({ className, ...props }, ref) => (
    <CheckboxPrimitive.Root
        ref={ref}
        className={cn(
            'peer h-4 w-4 shrink-0 rounded-sm border border-[var(--border-default)] bg-[var(--bg-surface)] transition-colors duration-200',
            'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[var(--primary-200)] focus-visible:border-[var(--border-focus)]',
            'disabled:cursor-not-allowed disabled:opacity-50',
            'data-[state=checked]:border-transparent data-[state=checked]:bg-[var(--bg-brand)]',
            'data-[state=indeterminate]:border-transparent data-[state=indeterminate]:bg-[var(--bg-brand)]',
            className
        )}
        {...props}
    >
        <CheckboxPrimitive.Indicator
            className="flex items-center justify-center text-[var(--text-inverse)]"
        >
            {props.checked === 'indeterminate' ? (
                <Minus className="h-3 w-3" strokeWidth={3} />
            ) : (
                <Check className="h-3 w-3" strokeWidth={3} />
            )}
        </CheckboxPrimitive.Indicator>
    </CheckboxPrimitive.Root>
))
Checkbox.displayName = CheckboxPrimitive.Root.displayName

export { Checkbox }
